/* eslint-disable react-hooks/exhaustive-deps */
/** @jsxImportSource @emotion/react */
import tw from 'twin.macro';
import { css } from '@emotion/react';

import { Modal } from 'components/UiControls/modal/modal';
import { Loader } from 'components/loader/loader';
import { isEmpty, isEqual, toNumber } from 'lodash';
import React, {
  ReactElement,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useToggle } from 'react-use';
import { RootState } from 'reducers';
import { CollectionService } from 'services/collection.service';
import { defaultMonetisationCollection } from 'utils/default/defaultSetting';
import { BasicConfirmModal } from 'utils/models/modal.model';
import { HTTPStatus, ModalOptions } from 'utils/enum';
import {
  AdsConfigOptions,
  BakedInConfig,
  BakedInDetectionMethod,
  BakedInOptions,
  CollectionMonetisation,
  JingleOptions,
  OnmnyMidrollOptions,
  SilenceOptions,
  TextSearchOptions,
} from 'utils/models';
import { customToast } from 'utils/toast.util';
import { LabelCheckbox } from '../LabelCheckbox';
import { Switcher } from '../Switcher';
import { ensureCollectionMoneFormat } from 'utils/adapter.util';
import { ConfirmModal } from '../ConfirmModal/ConfirmModal';
import { NumberInput } from '../NumberInput';
import { MonetisationTextSearch } from '../MonetisationTextSearch/MonetisationTextSearch';
import { BakedInAdvancedConfigModal } from './BakedInAdvancedConfigModal';
import {
  MonetisationSectionCard,
  MonetisationSectionBlockRow,
  MonetisationSectionBody,
  MonetisationSectionRow,
  MonetisationPlacementRow,
  SubRowLabel,
  monetisationDisabledSubItemsCss,
} from './MonetisationSectionCard';

import {
  buildBakedInConfigForSave,
  hasAnyDetectionMethodEnabled,
  isJingleMethodEnabled,
} from './monetisationAdvanced.util';

const DETECTION_METHOD_LABELS: Record<BakedInDetectionMethod, string> = {
  jingle: 'Jingle',
  startAndEndPhrases: 'Start & End Phrases',
  adTranscriptMatch: 'Full Ad Transcript Match',
  aiPowered: 'AI-Powered Detection',
};

type IProps = {};
interface IPromiseParams {
  resolve: (option: ModalOptions) => void;
  reject: (err: any) => void;
}
export const MonetisationModal = forwardRef(
  (props: IProps, ref: React.Ref<unknown>): ReactElement => {
    useImperativeHandle(ref, () => ({ show }));

    const collection = useSelector((state: RootState) => state.collection);

    const [isOpen, toggleOpen] = useToggle(false);
    const [currentCollectionId, setCurrentCollectionId] = useState<string>('');
    const [isLoading, toggleLoading] = useToggle(false);
    const [isApplyAll, toggleApplyAll] = useToggle(false);
    const [showBakedInAdvanced, toggleShowBakedInAdvanced] = useToggle(false);
    const [monetisationConfig, setMonetisationConfig] =
      useState<CollectionMonetisation>(defaultMonetisationCollection);

    const defaultConfigs = ensureCollectionMoneFormat({});

    const isEqualDefaultConfigs = isEqual(defaultConfigs, monetisationConfig);

    const promiseInfo = useRef<IPromiseParams>({
      resolve: () => { },
      reject: () => { },
    });

    const confirmModalRef = useRef<BasicConfirmModal>(null);

    useEffect(() => {
      if (!isOpen && isEmpty(currentCollectionId)) return;

      fetchMonetisationConfig();
    }, [currentCollectionId, isOpen]);

    const show = async ({
      collectionId,
    }: {
      collectionId: string;
    }): Promise<unknown> => {
      return new Promise((resolve, reject) => {
        promiseInfo.current = {
          resolve,
          reject,
        };

        setCurrentCollectionId(collectionId);
        toggleOpen(true);
      });
    };

    const fetchMonetisationConfig = async () => {
      try {
        toggleLoading(true);
        const response =
          await CollectionService.getCollectionMonetisation(
            currentCollectionId,
          );

        if (response.status === HTTPStatus.SUCCESS) {
          if (isEmpty(response?.data)) {
            setMonetisationConfig(defaultMonetisationCollection);
            return;
          }
          setMonetisationConfig(ensureCollectionMoneFormat(response?.data));
        }
      } catch (err) {
        console.log(err);
      } finally {
        toggleLoading(false);
      }
    };

    const hideModal = () => {
      toggleApplyAll(false);
      toggleOpen(false);
    };

    const handleChangeCheckbox = (key: keyof CollectionMonetisation) => {
      setMonetisationConfig({
        ...monetisationConfig,
        [key]: {
          ...monetisationConfig[key],
          isEnabled: !monetisationConfig[key].isEnabled,
        },
      });
    };

    const handleChangeOptions = (
      key: keyof CollectionMonetisation,
      optionKey: keyof (BakedInOptions &
        JingleOptions &
        AdsConfigOptions &
        OnmnyMidrollOptions &
        SilenceOptions &
        TextSearchOptions),
      newValue: string | boolean | number | string[],
    ) => {
      setMonetisationConfig({
        ...monetisationConfig,
        [key]: {
          ...monetisationConfig[key],
          [optionKey]: newValue,
        },
      });
    };

    const handleSaveMonetisationConfig = async (
      isApplyAll?: boolean,
      configOverride?: CollectionMonetisation,
    ) => {
      try {
        if (isApplyAll) {
          const result = await confirmModalRef.current?.show({
            title: 'Apply to all collections',
            message: (
              <div>
                <div>
                  This action will override any configuration setup done for
                  other collections.
                </div>
                <strong>Do you wish to continue?</strong>
              </div>
            ),
          });

          if (result !== ModalOptions.YES) return;
        }

        const listCollectionIds = isApplyAll
          ? collection.items.map((item) => item.id)
          : [currentCollectionId];

        const configToSend = ensureCollectionMoneFormat(
          configOverride ?? monetisationConfig,
        );

        const saveConfigsAsync = CollectionService.saveCollectionMonetisation({
          collection_ids: listCollectionIds,
          monetisation_cfg: {
            ...configToSend,
            bakedInConfig: buildBakedInConfigForSave(configToSend.bakedInConfig),
          },
        });

        customToast.promise(saveConfigsAsync, {
          loading: 'Saving monetisation configuration',
          success: 'Saved successfully',
          error: 'Failed to save monetisation configuration',
        });

        toggleLoading(true);
        await saveConfigsAsync;
      } catch (err) {
        console.log(err);
      } finally {
        toggleLoading(false);
      }
    };

    const handleResetDefault = async (
      e: React.MouseEvent<HTMLButtonElement>,
    ) => {
      e.preventDefault();
      e.stopPropagation();

      const result = await confirmModalRef.current?.show({
        title: 'Reset to default',
        message: (
          <div>
            <div>
              This action will override current configurations by its default
              values.
            </div>
            <strong>Do you wish to continue?</strong>
          </div>
        ),
      });

      if (result !== ModalOptions.YES) return;

      setMonetisationConfig(ensureCollectionMoneFormat({}));
      customToast.success('Done reset to default');
    };

    const bakedInEnabled = monetisationConfig.bakedInConfig.isEnabled;
    const { action, detectionMethods } =
      monetisationConfig.bakedInConfig.advanced;
    const enabledDetectionMethods = Object.entries(detectionMethods).filter(
      ([, method]) => method.isEnabled,
    );
    const shouldWrapDetectionMethods = enabledDetectionMethods.length >= 3;
    const primaryDetectionMethods = shouldWrapDetectionMethods
      ? enabledDetectionMethods.slice(0, -2)
      : enabledDetectionMethods;
    const wrappedDetectionMethods = shouldWrapDetectionMethods
      ? enabledDetectionMethods.slice(-2)
      : [];
    const jingleEnabled = monetisationConfig.jingleConfig.isEnabled;
    const silenceEnabled = monetisationConfig.silenceConfig.isEnabled;
    const omnyMidrollsEnabled = monetisationConfig.omnyMidrollsConfig.isEnabled;
    const textSearchEnabled = monetisationConfig.textSearchConfig.isEnabled;
    const handleOpenBakedInAdvanced = () => {
      toggleShowBakedInAdvanced(true);
    };

    const handleCancelBakedInAdvanced = () => {
      toggleShowBakedInAdvanced(false);
    };

    const handleSaveBakedInAdvanced = async (
      advanced: BakedInConfig['advanced'],
    ) => {
      // "Keep" allows saving with no detection method enabled, but warns
      // first — "Remove" already blocks this case inline in the Advanced
      // modal's own validation.
      if (
        advanced.action === 'keep' &&
        !hasAnyDetectionMethodEnabled(advanced)
      ) {
        const result = await confirmModalRef.current?.show({
          title: 'Save without a detection method',
          message: (
            <div>
              Saving without providing a detection method when chooing "What to do with the baked-in ad" - "Keep" might damage
              detection performance
            </div>
          ),
          confirmText: 'Continue',
        });

        if (result !== ModalOptions.YES) return;
      }

      const updatedConfig = {
        ...monetisationConfig,
        bakedInConfig: {
          ...monetisationConfig.bakedInConfig,
          advanced,
          // Legacy backend code (utils_bakedin_detection.py) still reads
          // jingleIdentification directly — keep it mirrored in FE state as
          // soon as the jingle method toggles, not only in the save payload
          // (buildBakedInConfigForSave already does that part).
          jingleIdentification: isJingleMethodEnabled(advanced),
        },
      };

      // Saving here must never reveal Monetisation Configuration or close any
      // modal — the Advanced modal stays open and visible.
      setMonetisationConfig(updatedConfig);
      handleSaveMonetisationConfig(isApplyAll, updatedConfig);
    };

    const bakedInAdvanced = monetisationConfig.bakedInConfig.advanced;

    return (
      <div css={customModalCss}>
        <Modal
          classes="library-mone-modal"
          show={isOpen && !showBakedInAdvanced}
          modalClosed={hideModal}
        >
          <div tw="text-14 px-3 pt-5">
            <div tw="font-bold text-[2.5rem]">Monetisation Configuration</div>

            <div tw="mt-6 relative">
              {isLoading && (
                <div tw="flex justify-center absolute top[0] left[0] w-full items-center h-full min-h-[20rem] opacity-[0.95] z-[1] bg-white rounded-lg">
                  <Loader />
                </div>
              )}

              <div tw="grid grid-cols-2 gap-6">
                <MonetisationSectionCard>
                  <MonetisationSectionRow>
                    <LabelCheckbox
                      checked={bakedInEnabled}
                      label="Baked in"
                      sectionHeaderStyle
                      checkboxAccent="purple"
                      onChange={() => handleChangeCheckbox('bakedInConfig')}
                    />
                    <button
                      type="button"
                      css={advancedLinkCss}
                      onClick={() => toggleShowBakedInAdvanced(true)}
                    >
                      Advanced
                    </button>
                  </MonetisationSectionRow>
                  <MonetisationSectionBody>
                    <MonetisationSectionRow
                      css={
                        !bakedInEnabled ? monetisationDisabledSubItemsCss : undefined
                      }
                    >
                      <SubRowLabel enabled={bakedInEnabled}>
                        Count as Midroll
                      </SubRowLabel>
                      <Switcher
                        hasLabel
                        checked={
                          monetisationConfig.bakedInConfig.countAsMidroll
                        }
                        onCheckedChange={(checked) =>
                          handleChangeOptions(
                            'bakedInConfig',
                            'countAsMidroll',
                            checked,
                          )
                        }
                      />
                    </MonetisationSectionRow>
                    <MonetisationPlacementRow
                      label="Place additional midrolls"
                      enabled={bakedInEnabled}
                      items={[
                        { label: 'Before', value: 'before' },
                        { label: 'After', value: 'after' },
                        { label: 'Anywhere', value: 'anywhere' },
                      ]}
                      onChange={(item) =>
                        handleChangeOptions(
                          'bakedInConfig',
                          'placement',
                          item.value,
                        )
                      }
                      defaultValue={monetisationConfig.bakedInConfig.placement}
                    />
                    <MonetisationSectionRow
                      css={
                        !bakedInEnabled ? monetisationDisabledSubItemsCss : undefined
                      }
                    >
                      <SubRowLabel enabled={bakedInEnabled}>
                        Detection method
                      </SubRowLabel>
                      <div tw="flex flex-wrap gap-2 items-center" data-detection-badges="">
                        <div tw="flex flex-wrap gap-2 w-full justify-end">
                          <span css={detectionBadgeCss('keep')}>
                            {action === 'remove' ? 'Remove' : 'Keep'}
                          </span>
                          {primaryDetectionMethods.map(([key], idx) => (
                            <span
                              key={key}
                              css={
                                shouldWrapDetectionMethods && idx > 0
                                  ? detectionMethodOwnLineCss
                                  : undefined
                              }
                            >
                              <span css={detectionBadgeCss(key)}>
                                {DETECTION_METHOD_LABELS[key as BakedInDetectionMethod]}
                              </span>
                            </span>
                          ))}
                        </div>
                        {wrappedDetectionMethods.length > 0 && (
                          <div tw="flex flex-col gap-2 w-full items-end xl-up:(flex-row justify-end items-center)">
                            {wrappedDetectionMethods.map(([key]) => (
                              <span key={key} css={detectionBadgeCss(key)}>
                                {DETECTION_METHOD_LABELS[key as BakedInDetectionMethod]}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </MonetisationSectionRow>
                  </MonetisationSectionBody>
                </MonetisationSectionCard>

                <MonetisationSectionCard>
                  <MonetisationSectionRow>
                    <LabelCheckbox
                      checked={jingleEnabled}
                      label="Jingle Configuration"
                      sectionHeaderStyle
                      checkboxAccent="purple"
                      onChange={() => handleChangeCheckbox('jingleConfig')}
                    />
                  </MonetisationSectionRow>
                  <MonetisationSectionBody>
                    <MonetisationSectionRow
                      css={
                        !jingleEnabled ? monetisationDisabledSubItemsCss : undefined
                      }
                    >
                      <SubRowLabel enabled={jingleEnabled}>
                        Always place Midroll
                      </SubRowLabel>
                      <Switcher
                        hasLabel
                        checked={
                          monetisationConfig.jingleConfig.alwaysPlaceMidroll
                        }
                        onCheckedChange={(checked) =>
                          handleChangeOptions(
                            'jingleConfig',
                            'alwaysPlaceMidroll',
                            checked,
                          )
                        }
                      />
                    </MonetisationSectionRow>
                    <MonetisationPlacementRow
                      label="Placement additional midrolls"
                      enabled={jingleEnabled}
                      items={[
                        { label: 'Before', value: 'before' },
                        { label: 'Between', value: 'between' },
                        { label: 'After', value: 'after' },
                      ]}
                      onChange={(item) =>
                        handleChangeOptions(
                          'jingleConfig',
                          'placement',
                          item.value,
                        )
                      }
                      defaultValue={monetisationConfig.jingleConfig.placement}
                    />
                    <MonetisationSectionRow
                      css={
                        !jingleEnabled ? monetisationDisabledSubItemsCss : undefined
                      }
                    >
                      <SubRowLabel enabled={jingleEnabled}>
                        PreRoll checked
                      </SubRowLabel>
                      <Switcher
                        hasLabel
                        checked={monetisationConfig.jingleConfig.isPrerollCheck}
                        onCheckedChange={(checked) =>
                          handleChangeOptions(
                            'jingleConfig',
                            'isPrerollCheck',
                            checked,
                          )
                        }
                      />
                    </MonetisationSectionRow>
                    <MonetisationSectionRow
                      css={
                        !jingleEnabled ? monetisationDisabledSubItemsCss : undefined
                      }
                    >
                      <SubRowLabel enabled={jingleEnabled}>
                        Postroll checked
                      </SubRowLabel>
                      <Switcher
                        hasLabel
                        checked={
                          monetisationConfig.jingleConfig.isPostrollCheck
                        }
                        onCheckedChange={(checked) =>
                          handleChangeOptions(
                            'jingleConfig',
                            'isPostrollCheck',
                            checked,
                          )
                        }
                      />
                    </MonetisationSectionRow>
                    <MonetisationPlacementRow
                      label="Detector"
                      enabled={jingleEnabled}
                      items={[
                        { label: 'Sonic', value: 'sonic' },
                        { label: 'Music', value: 'music' },
                      ]}
                      onChange={(item) =>
                        handleChangeOptions(
                          'jingleConfig',
                          'detector',
                          item.value,
                        )
                      }
                      defaultValue={monetisationConfig.jingleConfig.detector}
                    />
                  </MonetisationSectionBody>
                </MonetisationSectionCard>

                <MonetisationSectionCard>
                  <MonetisationSectionRow>
                    <LabelCheckbox
                      checked={silenceEnabled}
                      label="Silence Configuration"
                      sectionHeaderStyle
                      checkboxAccent="purple"
                      onChange={() => handleChangeCheckbox('silenceConfig')}
                    />
                  </MonetisationSectionRow>
                  <MonetisationSectionBody>
                    <MonetisationSectionRow
                      css={
                        !silenceEnabled ? monetisationDisabledSubItemsCss : undefined
                      }
                    >
                      <SubRowLabel enabled={silenceEnabled}>
                        Always place Midroll
                      </SubRowLabel>
                      <Switcher
                        hasLabel
                        checked={
                          monetisationConfig.silenceConfig.alwaysPlaceMidroll
                        }
                        onCheckedChange={(checked) =>
                          handleChangeOptions(
                            'silenceConfig',
                            'alwaysPlaceMidroll',
                            checked,
                          )
                        }
                      />
                    </MonetisationSectionRow>
                    <MonetisationSectionRow
                      css={
                        !silenceEnabled ? monetisationDisabledSubItemsCss : undefined
                      }
                    >
                      <SubRowLabel enabled={silenceEnabled}>
                        Silence duration
                      </SubRowLabel>
                      <div tw="flex items-center text-sonnant-dark">
                        <NumberInput
                          tw="(max-width[5.4rem] height[3rem] m-2)!"
                          value={
                            monetisationConfig.silenceConfig.silenceDuration
                          }
                          onChangeNumber={(value) =>
                            handleChangeOptions(
                              'silenceConfig',
                              'silenceDuration',
                              value,
                            )
                          }
                          onBlur={(e) =>
                            handleChangeOptions(
                              'silenceConfig',
                              'silenceDuration',
                              toNumber(e.target.value) || 3,
                            )
                          }
                          min={0.5}
                          step={0.5}
                          max={600}
                        />
                        seconds
                      </div>
                    </MonetisationSectionRow>
                  </MonetisationSectionBody>
                </MonetisationSectionCard>

                <MonetisationSectionCard>
                  <MonetisationSectionRow>
                    <LabelCheckbox
                      checked={omnyMidrollsEnabled}
                      label="CMS Midrolls Configuration"
                      sectionHeaderStyle
                      checkboxAccent="purple"
                      onChange={() =>
                        handleChangeCheckbox('omnyMidrollsConfig')
                      }
                    />
                  </MonetisationSectionRow>
                  <MonetisationSectionBody>
                    <MonetisationSectionRow
                      css={
                        !omnyMidrollsEnabled
                          ? monetisationDisabledSubItemsCss
                          : undefined
                      }
                    >
                      <SubRowLabel enabled={omnyMidrollsEnabled}>
                        Count as Midroll
                      </SubRowLabel>
                      <Switcher
                        hasLabel
                        checked={
                          monetisationConfig.omnyMidrollsConfig.countAsMidroll
                        }
                        onCheckedChange={(checked) =>
                          handleChangeOptions(
                            'omnyMidrollsConfig',
                            'countAsMidroll',
                            checked,
                          )
                        }
                      />
                    </MonetisationSectionRow>
                    <MonetisationPlacementRow
                      label="Place additional midrolls"
                      enabled={omnyMidrollsEnabled}
                      items={[
                        { label: 'Before', value: 'before' },
                        { label: 'After', value: 'after' },
                        { label: 'Anywhere', value: 'anywhere' },
                      ]}
                      onChange={(item) =>
                        handleChangeOptions(
                          'omnyMidrollsConfig',
                          'placement',
                          item.value,
                        )
                      }
                      defaultValue={
                        monetisationConfig.omnyMidrollsConfig.placement
                      }
                    />
                  </MonetisationSectionBody>
                </MonetisationSectionCard>

                <MonetisationSectionCard>
                  <MonetisationSectionRow>
                    <div tw="font-semibold text-14 text-sonnant-dark w-full">
                      Miscellaneous Ads Configuration
                    </div>
                  </MonetisationSectionRow>
                  <MonetisationSectionBody>
                    <MonetisationSectionBlockRow>
                      <span>
                        Insert{' '}
                        <NumberInput
                          tw="(max-width[5.5rem] height[3rem] m-2 inline-block)!"
                          value={monetisationConfig.multipleAdsConfig.bakedIn}
                          onChangeNumber={(value) =>
                            handleChangeOptions(
                              'multipleAdsConfig',
                              'bakedIn',
                              value,
                            )
                          }
                          onBlur={(e) =>
                            handleChangeOptions(
                              'multipleAdsConfig',
                              'bakedIn',
                              parseInt(e.target.value) || 1,
                            )
                          }
                          min={1}
                          step={1}
                          max={100}
                        />
                        ads per slot for each baked in ad identified
                      </span>
                    </MonetisationSectionBlockRow>
                    <MonetisationSectionBlockRow>
                      <span>
                        Insert{' '}
                        <NumberInput
                          tw="(max-width[5.5rem] height[3rem] m-2 inline-block)!"
                          value={monetisationConfig.multipleAdsConfig.other}
                          min={1}
                          step={1}
                          max={100}
                          onChangeNumber={(value) =>
                            handleChangeOptions(
                              'multipleAdsConfig',
                              'other',
                              value,
                            )
                          }
                          onBlur={(e) =>
                            handleChangeOptions(
                              'multipleAdsConfig',
                              'other',
                              parseInt(e.target.value) || 1,
                            )
                          }
                        />
                        ads per slot based on other configurations
                      </span>
                    </MonetisationSectionBlockRow>
                    <MonetisationSectionRow>
                      <SubRowLabel enabled>Topic change</SubRowLabel>
                      <Switcher
                        hasLabel
                        checked={
                          monetisationConfig.multipleAdsConfig.topicChanged
                        }
                        onCheckedChange={(checked) =>
                          handleChangeOptions(
                            'multipleAdsConfig',
                            'topicChanged',
                            checked,
                          )
                        }
                      />
                    </MonetisationSectionRow>
                  </MonetisationSectionBody>
                </MonetisationSectionCard>

                <MonetisationSectionCard>
                  <MonetisationSectionRow>
                    <LabelCheckbox
                      checked={textSearchEnabled}
                      label="Text Search Configuration"
                      sectionHeaderStyle
                      checkboxAccent="purple"
                      onChange={() => handleChangeCheckbox('textSearchConfig')}
                    />
                  </MonetisationSectionRow>
                  <MonetisationSectionBody>
                    <MonetisationSectionBlockRow
                      css={
                        !textSearchEnabled ? monetisationDisabledSubItemsCss : undefined
                      }
                    >
                      <MonetisationTextSearch
                        textList={monetisationConfig.textSearchConfig.terms}
                        onChange={(termList) =>
                          handleChangeOptions(
                            'textSearchConfig',
                            'terms',
                            termList,
                          )
                        }
                      />
                    </MonetisationSectionBlockRow>
                    <MonetisationPlacementRow
                      label="Placement additional midrolls"
                      enabled={textSearchEnabled}
                      items={[
                        { label: 'Before', value: 'before' },
                        { label: 'After', value: 'after' },
                      ]}
                      onChange={(item) =>
                        handleChangeOptions(
                          'textSearchConfig',
                          'placement',
                          item.value,
                        )
                      }
                      defaultValue={
                        monetisationConfig.textSearchConfig.placement
                      }
                    />
                  </MonetisationSectionBody>
                </MonetisationSectionCard>
              </div>
            </div>

            <div tw="mt-5 flex justify-between">
              <div tw="flex gap-x-4">
                <button
                  type="button"
                  className="button btn-secondary large apply"
                  tw="mb-0"
                  onClick={hideModal}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="button btn-secondary large apply"
                  tw="(mb-0 max-width[unset] width[unset] px-3.5)!"
                  onClick={handleResetDefault}
                  disabled={isEqualDefaultConfigs}
                >
                  Reset to default
                </button>
              </div>

              <div tw="flex gap-x-5">
                <LabelCheckbox
                  checked={isApplyAll}
                  label="Apply to all collections"
                  onChange={() => toggleApplyAll()}
                />
                <button
                  type="button"
                  className="button btn-primary large cancel"
                  onClick={() => handleSaveMonetisationConfig(isApplyAll)}
                  tw="mb-0"
                  disabled={isLoading}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </Modal>

        <BakedInAdvancedConfigModal
          show={isOpen && showBakedInAdvanced}
          collectionId={currentCollectionId}
          config={monetisationConfig.bakedInConfig}
          isSaving={isLoading}
          onCancel={handleCancelBakedInAdvanced}
          onSave={handleSaveBakedInAdvanced}
        />

        {/* Rendered outside both Modals above — Backdrop unmounts its
            children when `show` is false, so nesting this inside either
            Modal would drop confirmModalRef.current to null whenever that
            Modal is hidden (e.g. while the Advanced modal is the one open),
            silently no-opping every confirmModalRef.current?.show(...) call. */}
        <ConfirmModal ref={confirmModalRef} />
      </div>
    );
  },
);

const customModalCss = css`
  .Modal.library-mone-modal {
    ${tw`lg-up:(width[100rem] left[calc(50% - 50rem)]) xl-up:(width[120rem] left[calc(50% - 60rem)]) 2xl-up:(width[140rem] left[calc(50% - 70rem)])`}
  }
`;

const detectionMethodOwnLineCss = css`
  ${tw`lg-down:(flex w-full justify-end)`}
`;

const advancedLinkCss = css`
  ${tw`text-sonnant-purple-2 font-normal cursor-pointer bg-transparent border-none p-0 hover:underline`}
`;

const detectionBadgeCss = (variant: string) => {
  const base = tw`inline-flex items-center text-center px-3 py-1 rounded-full text-12 font-medium select-none border`;

  if (variant === 'keep' || variant === 'remove') {
    return [
      base,
      tw`border-sonnant-green text-sonnant-green`,
      keepBadgeFillCss,
    ];
  }

  return [
    base,
    tw`border-sonnant-purple-2 text-sonnant-purple-2`,
    jingleBadgeFillCss,
  ];
};
const keepBadgeFillCss = css`
  background-color: rgba(25, 175, 167, 0.12);
`;

const jingleBadgeFillCss = css`
  background-color: rgba(85, 81, 255, 0.1);
`;

